import React from 'react';
import LegalLayout from '../components/LegalLayout';

const TermsOfService = () => (
  <LegalLayout title="Terms of Service" lastUpdated="August 8, 2026">
    <p>
      These Terms of Service ("Terms") govern your access to and use of Create by Lumera and related services (the "Service") provided by Create by Lumera ("we", "us", "our") at <strong>createbylumera.com</strong>.
    </p>
    <p>By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.</p>

    <h2>1. Eligibility</h2>
    <p>You must be at least 16 years old to use the Service. By using the Service, you represent that you meet this requirement and have the legal capacity to enter into a binding agreement.</p>

    <h2>2. Account Registration</h2>
    <ul>
      <li>You must provide accurate, complete information when creating an account.</li>
      <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
      <li>You are responsible for all activity under your account.</li>
      <li>You must notify us immediately of any unauthorized use of your account.</li>
    </ul>

    <h2>3. The Service</h2>
    <p>Create by Lumera is an AI-powered platform for creating digital products including planners, journals, workbooks, recipe books, guides, and more. The Service includes:</p>
    <ul>
      <li>AI-assisted product generation using text prompts</li>
      <li>Export to PNG and PDF formats</li>
      <li>Collection management and sharing</li>
      <li>Third-party integrations (Pinterest, Shopify)</li>
    </ul>

    <h2>4. Free and Pro Plans</h2>
    <h3>4.1 Free Plan</h3>
    <p>The free plan provides limited access to the Service, including up to 3 product generations per month and basic export options.</p>

    <h3>4.2 Pro Plan</h3>
    <p>The Pro plan is a paid subscription providing unlimited access. See our <a href="/subscription-terms">Subscription Terms</a> for full details on pricing, billing, renewal, and cancellation.</p>

    <h2>5. Intellectual Property</h2>

    <h3>5.1 Your Content</h3>
    <p>You retain ownership of the prompts you provide and the customization choices you make. Digital products generated by the Service using AI are provided to you with a broad license for personal and commercial use, subject to the following:</p>
    <ul>
      <li>You may sell, distribute, and modify generated products.</li>
      <li>You may not claim the AI-generated output as solely human-created work where disclosure is legally required.</li>
      <li>You may not use generated content for illegal, harmful, or deceptive purposes.</li>
    </ul>

    <h3>5.2 Our Intellectual Property</h3>
    <p>The Service itself — including its design, code, brand, logos, and proprietary features — remains the property of Create by Lumera. You may not copy, modify, or reverse-engineer the Service.</p>

    <h2>6. Acceptable Use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Use the Service to generate illegal, abusive, hateful, or sexually explicit content</li>
      <li>Attempt to bypass usage limits, rate limits, or security measures</li>
      <li>Use automated scripts or bots to access the Service without permission</li>
      <li>Impersonate others or misrepresent your identity</li>
      <li>Resell or redistribute access to the Service itself</li>
      <li>Upload malware or harmful code</li>
    </ul>
    <p>We reserve the right to suspend or terminate accounts that violate these terms.</p>

    <h2>7. Third-Party Integrations</h2>
    <p>The Service allows you to connect with third-party services (Pinterest, Shopify, etc.). When you use these integrations:</p>
    <ul>
      <li>You are also subject to the third party's terms and privacy policies.</li>
      <li>We are not responsible for the availability or performance of third-party services.</li>
      <li>You authorize us to act on your behalf when publishing or exporting to connected services.</li>
    </ul>

    <h2>8. AI-Generated Content Disclaimer</h2>
    <p>
      Content generated by the Service is produced by artificial intelligence and may occasionally be inaccurate, incomplete, or unsuitable for your intended purpose. You are responsible for reviewing and editing all generated content before use. We make no guarantees regarding the accuracy, originality, or fitness of AI-generated content.
    </p>

    <h2>9. Limitation of Liability</h2>
    <p>
      To the maximum extent permitted by law, Create by Lumera shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including lost profits, revenue, data, or business opportunities, arising out of or related to your use of the Service.
    </p>
    <p>
      Our total liability for any claim arising from the Service shall not exceed the amount you paid to us in the 12 months preceding the claim.
    </p>

    <h2>10. Disclaimer of Warranties</h2>
    <p>
      The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
    </p>

    <h2>11. Termination</h2>
    <ul>
      <li>You may delete your account at any time.</li>
      <li>We may suspend or terminate your account for violation of these Terms or for any reason with reasonable notice.</li>
      <li>Upon termination, your right to use the Service ceases immediately. Generated content stored on our servers may be deleted after 30 days.</li>
    </ul>

    <h2>12. Governing Law</h2>
    <p>
      These Terms are governed by the laws of Norway, without regard to its conflict of law principles. Any disputes shall be resolved in the courts of Norway, unless mandatory consumer protection laws in your jurisdiction provide otherwise.
    </p>

    <h2>13. Changes to These Terms</h2>
    <p>
      We may update these Terms from time to time. We will notify you of material changes by email or through the Service. Continued use after changes take effect constitutes acceptance of the revised Terms.
    </p>

    <h2>14. Contact</h2>
    <p>Questions about these Terms? Contact us at <strong>support@createbylumera.com</strong>.</p>
  </LegalLayout>
);

export default TermsOfService;
